import React, { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import { FaPlus, FaTrash, FaSave } from 'react-icons/fa';
import { useApp } from '../../context/AppContext';
import {
  getAdminAppOrigins,
  createAppOrigin,
  updateAppOrigin,
  deleteAppOrigin,
} from '../../services/appOriginDefinitionService';
import { getAppOriginColor, normalizeAppOriginColor } from '../../services/appOriginService';
import AdminSettingsBackLink from '../../components/admin/AdminSettingsBackLink';

const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  padding: 2rem;
  background-color: var(--color-bg-secondary);
  min-height: 100vh;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  overflow-x: hidden;

  @media (max-width: 768px) {
    padding: 1rem;
  }
`;

const Panel = styled.section`
  background: var(--color-bg-primary);
  border-radius: 16px;
  padding: 1.5rem;
  border: 1px solid var(--color-border-primary);
  box-shadow: var(--shadow-sm);
  max-width: 100%;
  box-sizing: border-box;

  @media (max-width: 768px) {
    padding: 1rem;
  }
`;

const PageTitle = styled.h1`
  margin: 0 0 0.5rem;
  font-size: 1.5rem;
  font-weight: 650;
  letter-spacing: -0.02em;
  line-height: 1.2;
  color: var(--color-text-primary);
`;

const Hint = styled.p`
  margin: 0 0 1.25rem;
  color: var(--color-text-secondary);
  font-size: 0.9rem;
  line-height: 1.5;
`;

const inputStyles = `
  width: 100%;
  box-sizing: border-box;
  padding: 0.5rem 0.75rem;
  border: 1px solid var(--color-border-primary);
  border-radius: 10px;
  font-size: 0.9rem;
  font-family: inherit;
  color: var(--color-text-primary);
  background: var(--color-bg-primary);
  transition: border-color 0.2s, box-shadow 0.2s;

  &:focus {
    outline: none;
    border-color: var(--color-secondary);
    box-shadow: 0 0 0 2px rgba(178, 190, 195, 0.35);
  }

  &:disabled {
    background: var(--color-light);
    color: var(--color-text-muted);
    cursor: not-allowed;
  }
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
  gap: 1rem;
  margin-bottom: 1rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const FieldLabel = styled.label`
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  font-size: 0.8rem;
  font-weight: 600;
  color: var(--color-text-secondary);
`;

const StyledInput = styled.input`
  ${inputStyles}
`;

const TableWrap = styled.div`
  overflow-x: auto;
  width: 100%;
  max-width: 100%;
`;

const Table = styled.table`
  width: 100%;
  min-width: 720px;
  border-collapse: collapse;
  font-size: 0.9rem;

  th,
  td {
    padding: 0.75rem 0.5rem;
    text-align: left;
    border-bottom: 1px solid var(--color-border-primary);
    vertical-align: middle;
  }

  th {
    color: var(--color-text-secondary);
    font-weight: 600;
    font-size: 0.75rem;
    text-transform: uppercase;
    letter-spacing: 0.04em;
  }
`;

const TableInput = styled.input`
  ${inputStyles}
`;

const Button = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.55rem 1rem;
  border: 1px solid ${({ $variant }) =>
    $variant === 'danger'
      ? '#ef4444'
      : $variant === 'secondary'
        ? 'var(--color-border-primary)'
        : 'var(--color-btn-primary-border)'};
  border-radius: 10px;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  background: ${({ $variant }) =>
    $variant === 'danger'
      ? 'transparent'
      : $variant === 'secondary'
        ? 'var(--color-bg-tertiary)'
        : 'var(--color-btn-primary-bg)'};
  color: ${({ $variant }) =>
    $variant === 'danger'
      ? '#ef4444'
      : $variant === 'secondary'
        ? 'var(--color-text-primary)'
        : 'var(--color-btn-primary-text)'};
  transition: opacity 0.2s;
  box-sizing: border-box;

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const ActionsWrap = styled.div`
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
`;

const CodeCell = styled.td`
  display: flex;
  align-items: center;
  gap: 0.45rem;
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
  font-size: 0.82rem;
`;

const ColorFieldWrap = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const ColorInput = styled.input`
  width: 42px;
  height: 36px;
  padding: 0;
  border: 1px solid var(--color-border-primary);
  border-radius: 10px;
  cursor: pointer;
  background: transparent;
`;

const ColorHexInput = styled.input`
  ${inputStyles}
  max-width: 110px;
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
  text-transform: uppercase;
`;

const ColorPreview = styled.span`
  display: inline-block;
  width: 14px;
  height: 14px;
  border-radius: 999px;
  background: ${({ $color }) => $color || '#b2bec3'};
  border: 1px solid var(--color-border-primary);
  flex-shrink: 0;
`;

const Badge = styled.span`
  display: inline-block;
  margin-left: 0.4rem;
  padding: 0.1rem 0.45rem;
  border-radius: 999px;
  font-size: 0.65rem;
  font-weight: 700;
  text-transform: uppercase;
  background: var(--color-bg-tertiary);
  color: var(--color-text-secondary);
  border: 1px solid var(--color-border-primary);
`;

const emptyForm = () => ({
  code: '',
  label: '',
  sortOrder: 0,
  color: '#2563EB',
});

const ColorField = ({ value, onChange, code = '' }) => {
  const resolved = normalizeAppOriginColor(value, code);
  return (
    <ColorFieldWrap>
      <ColorInput
        type="color"
        value={resolved}
        onChange={(e) => onChange(e.target.value.toUpperCase())}
        aria-label="Selecionar cor da aplicação"
      />
      <ColorHexInput
        value={resolved}
        onChange={(e) => onChange(e.target.value.toUpperCase())}
        placeholder="#2563EB"
        maxLength={7}
      />
    </ColorFieldWrap>
  );
};

const AppOriginsAdminPage = () => {
  const { reloadAppOrigins, addToast } = useApp();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getAdminAppOrigins();
      setItems(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error(err);
      addToast?.('Erro ao carregar aplicações.', 'error');
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [addToast]);

  useEffect(() => {
    load();
  }, [load]);

  const refreshAll = async () => {
    await load();
    await reloadAppOrigins?.();
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await createAppOrigin({
        code: form.code,
        label: form.label,
        sortOrder: Number(form.sortOrder) || 0,
        color: normalizeAppOriginColor(form.color, form.code),
      });
      addToast?.('Aplicação criada.', 'success');
      setForm(emptyForm());
      await refreshAll();
    } catch (err) {
      const msg = err?.response?.data?.message || err?.message || 'Erro ao criar aplicação.';
      addToast?.(msg, 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleRowUpdate = async (row) => {
    try {
      await updateAppOrigin(row.id, {
        label: row.label,
        sortOrder: Number(row.sortOrder) || 0,
        active: row.active,
        color: normalizeAppOriginColor(row.color, row.code),
      });
      addToast?.('Aplicação atualizada.', 'success');
      await refreshAll();
    } catch (err) {
      const msg = err?.response?.data?.message || err?.message || 'Erro ao atualizar.';
      addToast?.(msg, 'error');
    }
  };

  const handleDelete = async (row) => {
    if (!window.confirm(`Remover a aplicação "${row.label}" (${row.code})?`)) return;
    try {
      await deleteAppOrigin(row.id);
      addToast?.('Aplicação removida.', 'success');
      await refreshAll();
    } catch (err) {
      const msg = err?.response?.data?.message || err?.message || 'Não foi possível remover.';
      addToast?.(msg, 'error');
    }
  };

  const patchRow = (id, field, value) => {
    setItems((prev) => prev.map((r) => (r.id === id ? { ...r, [field]: value } : r)));
  };

  return (
    <PageContainer>
      <AdminSettingsBackLink />
      <Panel>
        <PageTitle>Configurar aplicações (origem)</PageTitle>
        <Hint>
          Defina quais aplicações aparecem nos filtros, formulários e dashboards. Escolha uma cor para
          cada aplicação — ela aparece como borda lateral nos cards de tickets e nos indicadores da home.
          Origens em uso só podem ser desativadas, não removidas.
        </Hint>

        <form onSubmit={handleCreate}>
          <FormGrid>
            <FieldLabel>
              Código
              <StyledInput
                value={form.code}
                onChange={(e) => setForm((f) => ({ ...f, code: e.target.value.toUpperCase() }))}
                placeholder="EX.: TIME_TRACK"
                required
              />
            </FieldLabel>
            <FieldLabel>
              Rótulo
              <StyledInput
                value={form.label}
                onChange={(e) => setForm((f) => ({ ...f, label: e.target.value }))}
                placeholder="Time Track"
                required
              />
            </FieldLabel>
            <FieldLabel>
              Ordem
              <StyledInput
                type="number"
                value={form.sortOrder}
                onChange={(e) => setForm((f) => ({ ...f, sortOrder: e.target.value }))}
              />
            </FieldLabel>
            <FieldLabel>
              Cor
              <ColorField
                value={form.color}
                code={form.code}
                onChange={(color) => setForm((f) => ({ ...f, color }))}
              />
            </FieldLabel>
          </FormGrid>

          <Button type="submit" disabled={saving}>
            <FaPlus />
            Adicionar aplicação
          </Button>
        </form>
      </Panel>

      <Panel>
        {loading ? (
          <p>Carregando...</p>
        ) : (
          <TableWrap>
            <Table>
              <thead>
                <tr>
                  <th>Código</th>
                  <th>Rótulo</th>
                  <th>Cor</th>
                  <th>Ordem</th>
                  <th>Ativo</th>
                  <th>Tickets</th>
                  <th>Ações</th>
                </tr>
              </thead>
              <tbody>
                {items.map((row) => (
                  <tr key={row.id}>
                    <CodeCell>
                      <ColorPreview $color={getAppOriginColor(row.code)} />
                      {row.code}
                      {row.system && <Badge $system>sistema</Badge>}
                    </CodeCell>
                    <td>
                      <TableInput
                        value={row.label}
                        onChange={(e) => patchRow(row.id, 'label', e.target.value)}
                      />
                    </td>
                    <td>
                      <ColorField
                        value={row.color}
                        code={row.code}
                        onChange={(color) => patchRow(row.id, 'color', color)}
                      />
                    </td>
                    <td>
                      <TableInput
                        type="number"
                        style={{ maxWidth: 80 }}
                        value={row.sortOrder}
                        onChange={(e) => patchRow(row.id, 'sortOrder', e.target.value)}
                      />
                    </td>
                    <td>
                      <input
                        type="checkbox"
                        checked={row.active}
                        onChange={(e) => patchRow(row.id, 'active', e.target.checked)}
                        style={{ width: 18, height: 18, accentColor: '#28a745' }}
                      />
                      {!row.active && <Badge $inactive>inativo</Badge>}
                    </td>
                    <td>{row.ticketCount ?? 0}</td>
                    <td>
                      <ActionsWrap>
                        <Button type="button" $variant="secondary" onClick={() => handleRowUpdate(row)}>
                          <FaSave />
                          Salvar
                        </Button>
                        {!row.system && (
                          <Button type="button" $variant="danger" onClick={() => handleDelete(row)}>
                            <FaTrash />
                          </Button>
                        )}
                      </ActionsWrap>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </TableWrap>
        )}
      </Panel>
    </PageContainer>
  );
};

export default AppOriginsAdminPage;
